import { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { 
  Calendar, 
  ArrowLeft, 
  ChevronLeft, 
  ChevronRight, 
  X, 
  Maximize2,
  Building,
  Image as ImageIcon
} from 'lucide-react';
import { newsService } from '../services/newsService';
import type { NewsArticle } from '../types/news';
import { LazySection } from '../components/ui/LazySection';

export function NewsDetail() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const [article, setArticle] = useState<NewsArticle | null | undefined>(() => {
    if (!slug) return null;
    return newsService.getInitialNews().find((n) => n.slug === slug || n.id === slug) || undefined;
  });
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  useEffect(() => {
    let isMounted = true;
    if (slug) {
      newsService.getNewsBySlug(slug)
        .then((found) => {
          if (isMounted) {
            setArticle(found || null);
          }
        })
        .catch(() => {
          if (isMounted) setArticle(null);
        });
    }

    return () => {
      isMounted = false;
    };
  }, [slug]);

  // Gallery keyboard navigation
  const handlePrevImage = useCallback(() => {
    if (lightboxIndex === null || !article?.gallery?.length) return;
    setLightboxIndex((prev) => (prev! === 0 ? article.gallery!.length - 1 : prev! - 1));
  }, [lightboxIndex, article]);

  const handleNextImage = useCallback(() => {
    if (lightboxIndex === null || !article?.gallery?.length) return;
    setLightboxIndex((prev) => (prev! === article.gallery!.length - 1 ? 0 : prev! + 1));
  }, [lightboxIndex, article]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (lightboxIndex === null) return;
      if (e.key === 'Escape') setLightboxIndex(null);
      if (e.key === 'ArrowLeft') handlePrevImage();
      if (e.key === 'ArrowRight') handleNextImage();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxIndex, handlePrevImage, handleNextImage]);

  // Prevent body scroll when lightbox is open
  useEffect(() => {
    if (lightboxIndex !== null) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [lightboxIndex]);

  // Loading state
  if (article === undefined) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center text-zinc-400">
        <div className="text-center">
          <div className="inline-block w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin mb-4" />
          <p className="font-neocat">Cargando noticia...</p>
        </div>
      </div>
    );
  }

  // Not found state
  if (article === null) {
    return (
      <div className="min-h-[70vh] bg-zinc-950 flex flex-col items-center justify-center px-4 text-center">
        <h2 className="font-neocat text-3xl font-bold text-zinc-100 mb-4">Noticia no encontrada</h2>
        <p className="font-neocat text-zinc-400 max-w-md mb-8">
          La publicación que buscas no existe o ha sido trasladada.
        </p>
        <Link
          to="/noticias"
          className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-zinc-800 hover:bg-zinc-700 text-white font-neocat text-sm tracking-wider uppercase transition-colors"
        >
          <ArrowLeft size={16} />
          <span>Volver al listado de noticias</span>
        </Link>
      </div>
    );
  }

  const gallery = article.gallery || [];

  return (
    <article className="min-h-screen bg-zinc-950 text-zinc-300 pb-28">
      {/* Top Header / Breadcrumbs */}
      <div className="border-b border-white/10 bg-zinc-950/80 backdrop-blur-md">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/noticias')}
            className="inline-flex items-center gap-2 text-xs font-neocat tracking-widest uppercase text-zinc-400 hover:text-white transition-colors"
          >
            <ArrowLeft size={15} />
            <span>Volver a Noticias</span>
          </button>

          {/* Breadcrumbs */}
          <nav className="hidden sm:flex items-center gap-2 text-xs font-neocat text-zinc-500">
            <Link to="/" className="hover:text-zinc-300 transition-colors">Inicio</Link>
            <span>/</span>
            <Link to="/noticias" className="hover:text-zinc-300 transition-colors">Noticias</Link>
            <span>/</span>
            <span className="text-zinc-400 truncate max-w-[200px]">{article.title}</span>
          </nav>
        </div>
      </div>

      {/* Article Header & Cover */}
      <header className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-8">
        {/* Meta badges */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          {article.category && (
            <span className="px-3.5 py-1 rounded-full text-xs font-neocat tracking-widest uppercase bg-gradient-to-r from-zinc-300 via-zinc-100 to-zinc-400 text-zinc-950 font-bold shadow-md shadow-white/5">
              {article.category}
            </span>
          )}
          <span className="inline-flex items-center gap-1.5 text-xs text-zinc-400 font-neocat">
            <Calendar size={14} className="text-zinc-400" />
            {article.displayDate}
          </span>
          {article.author && (
            <span className="hidden sm:inline-flex items-center gap-1.5 text-xs text-zinc-500 font-neocat">
              <Building size={14} />
              {article.author}
            </span>
          )}
        </div>

        {/* Title */}
        <h1 className="font-neocat text-3xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-zinc-100 mb-6 leading-tight drop-shadow-md">
          {article.title}
        </h1>

        {article.subtitle && (
          <p className="font-neocat text-lg sm:text-xl text-zinc-300 font-light leading-relaxed mb-8 max-w-4xl border-l-2 border-zinc-400/80 pl-4 py-1">
            {article.subtitle}
          </p>
        )}

        {/* Cover Image */}
        <div className="relative w-full rounded-2xl overflow-hidden border border-white/10 shadow-2xl bg-zinc-900 group">
          <div className="aspect-[16/9] sm:aspect-[21/9] max-h-[560px] w-full">
            <img
              src={article.coverImage}
              alt={article.coverImageAlt || article.title}
              className="w-full h-full object-cover object-center transform group-hover:scale-[1.02] transition-transform duration-700 ease-out"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-t from-zinc-950/80 via-transparent to-transparent pointer-events-none" />
          <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between text-xs font-neocat text-zinc-300 drop-shadow">
            <span>{article.coverImageAlt || article.title}</span>
            <span className="hidden sm:inline-block bg-black/40 backdrop-blur-sm px-3 py-1 rounded border border-white/10">
              Foto de Portada
            </span>
          </div>
        </div>
      </header>

      {/* Article Body Content */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="space-y-8 font-neocat text-zinc-300 text-base sm:text-lg leading-relaxed font-light text-justify">
          {article.content.map((paragraph, index) => (
            <p
              key={index}
              className={
                index === 0
                  ? 'first-letter:text-5xl first-letter:font-bold first-letter:text-white first-letter:float-left first-letter:mr-3 first-letter:leading-none text-zinc-200'
                  : ''
              }
            >
              {paragraph}
            </p>
          ))}
        </div>
      </section>

      {/* Gallery Section */}
      {gallery.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-16 pt-16 border-t border-white/10">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/5 border border-white/10 text-zinc-400 text-xs tracking-widest uppercase mb-3">
              <ImageIcon size={14} className="text-zinc-300" />
              <span>Registro Fotográfico</span>
            </div>
            <h2 className="font-neocat text-2xl sm:text-4xl font-bold tracking-widest uppercase bg-gradient-to-r from-zinc-400 via-zinc-100 to-zinc-500 bg-clip-text text-transparent mb-3">
              Galería de la Celebración
            </h2>
            <p className="font-neocat text-sm text-zinc-400 font-light max-w-xl mx-auto">
              Momentos de gracia y solemne alegría vividos durante la ceremonia en la Catedral Metropolitana.
            </p>
          </div>

          <LazySection>
            {/* Bento Grid layout for gallery */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 auto-rows-[280px]">
              {gallery.map((item, idx) => {
                // Give varying span classes for dynamic visual rhythm
                const spanClass =
                  idx === 0
                    ? 'sm:col-span-2 lg:col-span-2 sm:row-span-2'
                    : idx === 3
                    ? 'sm:col-span-2 lg:col-span-1'
                    : 'col-span-1';

                return (
                  <div
                    key={idx}
                    onClick={() => setLightboxIndex(idx)}
                    className={`group relative rounded-2xl overflow-hidden bg-zinc-900 border border-white/10 hover:border-white/30 cursor-pointer transition-all duration-500 shadow-xl ${spanClass}`}
                  >
                    <img
                      src={item.src}
                      alt={item.alt || `Fotografía ${idx + 1}`}
                      className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700 ease-out"
                      loading="lazy"
                    />
                    
                    {/* Dark gradient overlay on hover */}
                    <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-6">
                      <div className="flex items-center justify-between text-white">
                        <span className="font-neocat text-sm tracking-wide font-medium drop-shadow">
                          {item.caption || item.alt || 'Ver fotografía'}
                        </span>
                        <div className="w-9 h-9 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center border border-white/30">
                          <Maximize2 size={16} />
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </LazySection>
        </section>
      )}

      {/* Lightbox Modal */}
      {lightboxIndex !== null && gallery[lightboxIndex] && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 backdrop-blur-xl flex flex-col justify-between p-4 sm:p-8 animate-in fade-in duration-200"
          onClick={() => setLightboxIndex(null)}
        >
          {/* Top Bar */}
          <div className="flex items-center justify-between z-10 w-full max-w-6xl mx-auto" onClick={(e) => e.stopPropagation()}>
            <div className="text-zinc-400 font-neocat text-xs sm:text-sm tracking-wider">
              <span className="text-white font-semibold">{lightboxIndex + 1}</span> / {gallery.length}
            </div>

            <button
              onClick={() => setLightboxIndex(null)}
              className="p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors border border-white/20"
              aria-label="Cerrar galería"
            >
              <X size={22} />
            </button>
          </div>

          {/* Central Image Container */}
          <div className="relative flex-1 flex items-center justify-center my-2 max-w-6xl mx-auto w-full">
            {/* Prev Button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                handlePrevImage();
              }}
              className="absolute left-2 sm:left-4 z-20 p-3 rounded-full bg-zinc-900/80 hover:bg-zinc-800 text-white border border-white/20 transition-all shadow-xl hover:scale-105"
              aria-label="Anterior imagen"
            >
              <ChevronLeft size={24} />
            </button>

            {/* Image */}
            <div 
              className="relative max-h-[75vh] max-w-full flex items-center justify-center"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={gallery[lightboxIndex].src}
                alt={gallery[lightboxIndex].alt || 'Imagen ampliada'}
                className="max-h-[75vh] max-w-full object-contain rounded-lg shadow-2xl border border-white/10"
              />
            </div>

            {/* Next Button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleNextImage();
              }}
              className="absolute right-2 sm:right-4 z-20 p-3 rounded-full bg-zinc-900/80 hover:bg-zinc-800 text-white border border-white/20 transition-all shadow-xl hover:scale-105"
              aria-label="Siguiente imagen"
            >
              <ChevronRight size={24} />
            </button>
          </div>

          {/* Bottom Caption */}
          <div className="text-center z-10 max-w-2xl mx-auto" onClick={(e) => e.stopPropagation()}>
            {gallery[lightboxIndex].caption && (
              <p className="font-neocat text-sm sm:text-base text-zinc-200 bg-zinc-900/80 px-6 py-2 rounded-full border border-white/10 backdrop-blur-md">
                {gallery[lightboxIndex].caption}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Bottom Back Button & Navigation */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mt-16 text-center">
        <Link
          to="/noticias"
          className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-zinc-900 hover:bg-zinc-800 border border-white/10 hover:border-white/30 text-white font-neocat text-xs tracking-widest uppercase transition-all duration-300 shadow-xl"
        >
          <ArrowLeft size={16} />
          <span>Explorar más noticias del Seminario</span>
        </Link>
      </div>
    </article>
  );
}
export default NewsDetail;
